import {
  useState,
  useMemo,
} from "react";

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  Button,
  PageContainer,
} from "@swot/base-components";

import {
  CmtsSmartDataGrid,
  type CmtsGridColumn,
} from "../data-grid/index.js";

import {
  CmtsApiErrorPanel,
} from "../CmtsApiErrorPanel.js";

import {
  CmtsEnterpriseModal,
} from "../CmtsEnterpriseModal.js";

import {
  CmtsVerificationForm,
} from "./CmtsVerificationForm.js";

import {
  verificationApi,
} from "../../services/verificationApi.js";

import {
  complaintDropdownApi,
} from "../../services/complaintDropdownApi.js";

import {
  createIdempotencyKey,
} from "../../services/api.service.js";

import type {
  VerificationCreatePayload,
} from "../../core/payloads/cmtsTypedPayloads.js";

import type {
  VerificationDto,
} from "../../../types/verification.types.js";

import type {
  DropdownOption,
} from "../../types/api.types.js";

import {
  invalidateCmtsLifecycleMutation,
} from "../../core/query/cmtsLifecycleInvalidation.js";

import {
  useCmtsSelector,
} from "../../store/hooks.js";

import {
  selectEffectivePermissions,
} from "../../store/selectors.js";

export type CmtsVerificationPanelProps =
  Readonly<{
    selectedRecordId?:
      | string
      | undefined;
  }>;
type VerificationGridRow =
  VerificationDto & {
    verificationActions?:
      | string
      | undefined;
  };
type VerificationActionCardProps = {
  icon: string;
  title: string;
  description: string;
  disabled?: boolean | undefined;
  danger?: boolean | undefined;
  onClick: () => void;
};

const queryKeys = {
  list: (
    complaintId: string,
  ) =>
    [
      "cmts",
      "verification",
      complaintId,
    ] as const,

  queue: [
    "cmts",
    "verification",
    "queue",
  ] as const,

  masters: [
    "cmts",
    "verification",
    "masters",
  ] as const,
};

function dateTime(
  value:
    | string
    | null
    | undefined,
): string {
  if (!value) {
    return "-";
  }

  const date = new Date(value);

  return Number.isNaN(
    date.getTime(),
  )
    ? value
    : date.toLocaleString(
        undefined,
        {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        },
      );
}

function statusCode(
  row: VerificationDto,
): string {
  return String(
    row.verificationStatusCode ??
    "",
  )
    .trim()
    .toUpperCase();
}

function statusName(
  row: VerificationDto,
): string {
  return String(
    row.verificationStatusName ??
    row.verificationStatusCode ??
    "PENDING",
  )
    .trim()
    .toUpperCase();
}

function statusIs(
  row: VerificationDto,
  ...codes: string[]
): boolean {
  const currentStatus =
    statusCode(row);

  return codes
    .map((code) =>
      code.toUpperCase(),
    )
    .includes(currentStatus);
}

function isDecisionCompleted(
  row: VerificationDto,
): boolean {
  return statusIs(
    row,
    "PASSED",
    "PASS",
    "VERIFIED",
    "EFFECTIVE",
    "FAILED",
    "FAIL",
    "REJECTED",
    "INEFFECTIVE",
    "COMPLETED",
    "CLOSED",
  );
}

function optionByCodes(
  options:
    | readonly DropdownOption[]
    | undefined,
  ...codes: string[]
): DropdownOption | undefined {
  const expected =
    codes.map((code) =>
      code.toUpperCase(),
    );

  return options?.find(
    (option) =>
      expected.includes(
        option.code?.toUpperCase() ??
        "",
      ) ||
      expected.includes(
        option.label.toUpperCase(),
      ),
  );
}

function VerificationActionCard({
  icon,
  title,
  description,
  disabled = false,
  danger = false,
  onClick,
}: VerificationActionCardProps) {
  return (
    <button
      type="button"
      className={[
        "cmts-notification-action-option",
        danger
          ? "cmts-notification-action-option--danger"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      disabled={disabled}
      onClick={onClick}
    >
      <span
        className="cmts-notification-action-option__icon"
        aria-hidden="true"
      >
        {icon}
      </span>

      <span className="cmts-notification-action-option__content">
        <strong>{title}</strong>

        <small>
          {description}
        </small>
      </span>

      <span
        className="cmts-notification-action-option__arrow"
        aria-hidden="true"
      >
        ›
      </span>
    </button>
  );
}

export function CmtsVerificationPanel(
  _props:
    CmtsVerificationPanelProps,
) {
  const {
    complaintId = "",
  } = useParams<{
    complaintId?: string;
  }>();

  const navigate =
    useNavigate();

  const queryClient =
    useQueryClient();

  const permissions =
    useCmtsSelector(
      selectEffectivePermissions,
    );

  const can = (
    code: string,
  ): boolean =>
    permissions.includes(code);

  const [
    createOpen,
    setCreateOpen,
  ] = useState(false);

const [
  actionsFor,
  setActionsFor,
] =
  useState<VerificationGridRow | null>(
    null,
  );

  const [
    actionError,
    setActionError,
  ] = useState<unknown>(null);

  const list = useQuery({
    queryKey: complaintId
      ? queryKeys.list(
          complaintId,
        )
      : queryKeys.queue,

    queryFn: ({ signal }) =>
      complaintId
        ? verificationApi.list(
            complaintId,
            signal,
          )
        : verificationApi.queue(
            signal,
          ),
  });

  const rules = useQuery({
    queryKey: [
      ...queryKeys.masters,
      "rules",
    ],

    queryFn: ({ signal }) =>
      complaintDropdownApi
        .verificationRules(
          {},
          signal,
        ),

    enabled:
      Boolean(complaintId),
  });

  const statuses = useQuery({
    queryKey: [
      ...queryKeys.masters,
      "statuses",
    ],

    queryFn: ({ signal }) =>
      complaintDropdownApi.master(
        "verification-status",
        {},
        signal,
      ),
  });

  const verifiers = useQuery({
    queryKey: [
      ...queryKeys.masters,
      "verifiers",
      complaintId,
    ],

    queryFn: ({ signal }) =>
      verificationApi
        .permittedVerifiers(
          complaintId,
          signal,
        ),

    enabled:
      Boolean(complaintId),
  });

  const refresh =
    async (): Promise<void> => {
      if (complaintId) {
        await invalidateCmtsLifecycleMutation(
          queryClient,
          complaintId,
          "verification",
        );
      } else {
        await queryClient
          .invalidateQueries({
            queryKey:
              queryKeys.queue,
          });
      }
    };

  const create = useMutation({
    mutationFn: (
      payload:
        VerificationCreatePayload,
    ) =>
      verificationApi.create(
        payload.complaintId,
        payload,
        createIdempotencyKey(),
      ),

    onSuccess: async () => {
      setCreateOpen(false);
      await refresh();
    },

    onError: (
      error: unknown,
    ) => {
      setActionError(error);
    },
  });

  const decision = useMutation({
    mutationFn: async ({
      action,
      row,
    }: {
      action: "pass" | "fail";
      row: VerificationDto;
    }) => {
      const status =
        action === "pass"
          ? optionByCodes(
              statuses.data,
              "PASSED",
              "PASS",
              "VERIFIED",
              "EFFECTIVE",
            )
          : optionByCodes(
              statuses.data,
              "FAILED",
              "FAIL",
              "REJECTED",
              "INEFFECTIVE",
            );

      if (!status) {
        throw new Error(
          `The governed ${action} verification status is unavailable.`,
        );
      }

 const payload: {
  verificationStatusId: string;
  rowVersion?: string | undefined;
} = {
  verificationStatusId:
    status.value,
};

if (row.rowVersion) {
  payload.rowVersion =
    row.rowVersion;
}

return verificationApi[
  action
](
  row.complaintId,
  row.id,
  payload,
  createIdempotencyKey(),
);
    },

    onSuccess: async () => {
      setActionsFor(null);
      setActionError(null);
      await refresh();
    },

    onError: (
      error: unknown,
    ) => {
      setActionError(error);
    },
  });

  const runDecision = async (
    action: "pass" | "fail",
    row: VerificationDto,
  ): Promise<void> => {
    setActionError(null);

    try {
      await decision.mutateAsync({
        action,
        row,
      });
    } catch {
      /*
       * Mutation error is displayed
       * inside the actions modal.
       */
    }
  };

const columns:
  readonly CmtsGridColumn<
    VerificationGridRow
  >[] = [
      {
        id: "verificationRuleName",
        header:
          "Verification Rule",
        sortable: true,
        filterable: true,
        minWidth: 210,

        render: (row) => (
          <div className="cmts-notification-title">
            <strong>
              {row.verificationRuleName ||
                "Verification rule"}
            </strong>

            <span>
              {String(
                row.id,
              )}
            </span>
          </div>
        ),
      },
      {
        id: "verifierName",
        header: "Verifier",
        sortable: true,
        filterable: true,
        minWidth: 170,

        render: (row) =>
          row.verifierName ||
          "Unassigned",
      },
      {
        id: "verificationStatusName",
        header: "Status",
        sortable: true,
        filterable: true,
        minWidth: 130,

        render: (row) => (
          <span
            className={[
              "cmts-status-chip",
              `cmts-verification-status--${statusCode(
                row,
              ).toLowerCase()}`,
            ].join(" ")}
          >
            {statusName(row)}
          </span>
        ),
      },
      {
        id: "remarks",
        header: "Remarks",
        sortable: false,
        filterable: true,
        minWidth: 230,

        render: (row) =>
          row.remarks || "-",
      },
      {
        id: "verifiedAt",
        header: "Verified At",
        sortable: true,
        minWidth: 170,

        accessor: (row) =>
          dateTime(
            row.verifiedAt,
          ),
      },
      {
        id: "rejectedAt",
        header: "Rejected At",
        sortable: true,
        minWidth: 170,

        accessor: (row) =>
          dateTime(
            row.rejectedAt,
          ),
      },
      {
        id: "verificationActions",
        header: "Actions",
        sortable: false,
        filterable: false,
        minWidth: 125,

        render: (row) => (
          <button
            type="button"
            className="cmts-notification-actions-button"
            aria-label="Open verification actions"
            onClick={(event) => {
              event.stopPropagation();
              setActionError(null);
              setActionsFor(row);
            }}
          >
            <span
              className="cmts-notification-actions-button__icon"
              aria-hidden="true"
            >
              ⋮
            </span>

            <span>Actions</span>
          </button>
        ),
      },
    ];

  const [activeFilter, setActiveFilter] = useState<"ALL" | "PENDING" | "PASSED" | "REJECTED">("ALL");

  const rawRows = (list.data ?? []) as readonly VerificationDto[];

  const metrics = useMemo(() => {
    let pending = 0;
    let passed = 0;
    let rejected = 0;

    for (const r of rawRows) {
      const code = statusCode(r);
      if (code === "PASSED" || code === "EFFECTIVE" || code === "COMPLETED" || code === "CLOSED" || code === "SUCCESSFUL") {
        passed++;
      } else if (code === "REJECTED" || code === "FAILED" || code === "NOT_EFFECTIVE") {
        rejected++;
      } else {
        pending++;
      }
    }

    return {
      total: rawRows.length,
      pending,
      passed,
      rejected,
    };
  }, [rawRows]);

  const displayRows = useMemo(() => {
    if (activeFilter === "ALL") return rawRows;
    if (activeFilter === "PENDING") return rawRows.filter((r) => {
      const code = statusCode(r);
      return code !== "PASSED" && code !== "EFFECTIVE" && code !== "COMPLETED" && code !== "CLOSED" && code !== "SUCCESSFUL" && code !== "REJECTED" && code !== "FAILED" && code !== "NOT_EFFECTIVE";
    });
    if (activeFilter === "PASSED") return rawRows.filter((r) => {
      const code = statusCode(r);
      return code === "PASSED" || code === "EFFECTIVE" || code === "COMPLETED" || code === "CLOSED" || code === "SUCCESSFUL";
    });
    if (activeFilter === "REJECTED") return rawRows.filter((r) => {
      const code = statusCode(r);
      return code === "REJECTED" || code === "FAILED" || code === "NOT_EFFECTIVE";
    });
    return rawRows;
  }, [rawRows, activeFilter]);

  const handleExportSummary = () => {
    const data = displayRows.map((r) => ({
      "Rule Name": r.verificationRuleName || "Verification rule",
      "Verifier": r.verifierName || "Unassigned",
      "Status": statusName(r),
      "Remarks": r.remarks || "",
      "Verified At": dateTime(r.verifiedAt),
      "Rejected At": dateTime(r.rejectedAt),
      "Complaint ID": r.complaintId || "",
    }));

    const jsonStr = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonStr], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Verification_Summary_${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const pendingStatusId =
    optionByCodes(
      statuses.data,
      "PENDING",
      "OPEN",
      "PLANNED",
    )?.value ?? "";

  const error =
    actionError ??
    list.error ??
    rules.error ??
    statuses.error ??
    verifiers.error;

  const canCreate =
    Boolean(
      can("VERIFICATION.CREATE") &&
      rules.data?.length &&
      verifiers.data?.length &&
      pendingStatusId,
    );

  const selectedDecisionCompleted =
    actionsFor !== null &&
    isDecisionCompleted(
      actionsFor,
    );

  const selectedCanPass =
    actionsFor !== null &&
    can(
      "VERIFICATION.MARK_SUCCESSFUL",
    ) &&
    !selectedDecisionCompleted;

  const selectedCanFail =
    actionsFor !== null &&
    can(
      "VERIFICATION.MARK_FAILED",
    ) &&
    !selectedDecisionCompleted;

  const busy =
    create.isPending ||
    decision.isPending;

  return (
    <PageContainer
      title="Verification"
      subtitle={
        complaintId
          ? "Complaint-scoped verification using governed rule, verifier and status identifiers."
          : "Verifier-scoped queue containing persisted complaint and verification identifiers."
      }
    >
      {error &&
      !actionsFor ? (
        <CmtsApiErrorPanel
          error={error}
          title="Verification operation failed"
          onRetry={() =>
            void list.refetch()
          }
        />
      ) : null}

      <p className="cmts-domain-guidance">
        Self-verification is
        separation-of-duties blocked.
        Select a different verifier.
        The backend independently
        validates role, permission,
        organization scope and
        separation of duties.
      </p>

      {/* Verifier KPI Metric Cards */}
      <div
        className="cmts-kpi-card-grid"
        style={{
          marginBottom: "1.25rem",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: "0.75rem",
        }}
      >
        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "ALL" ? "2px solid #3b82f6" : undefined,
            background: activeFilter === "ALL" ? "rgba(59, 130, 246, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("ALL")}
        >
          <span className="cmts-kpi-card__label">Total Queue</span>
          <strong>{metrics.total}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>All verification items</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "PENDING" ? "2px solid #f59e0b" : undefined,
            background: activeFilter === "PENDING" ? "rgba(245, 158, 11, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("PENDING")}
        >
          <span className="cmts-kpi-card__label">Pending Decision</span>
          <strong style={{ color: "#f59e0b" }}>{metrics.pending}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>Awaiting review</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "PASSED" ? "2px solid #10b981" : undefined,
            background: activeFilter === "PASSED" ? "rgba(16, 185, 129, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("PASSED")}
        >
          <span className="cmts-kpi-card__label">Passed / Effective</span>
          <strong style={{ color: "#10b981" }}>{metrics.passed}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: "#64748b" }}>Successfully verified</span>
        </button>

        <button
          type="button"
          className="cmts-kpi-card"
          style={{
            cursor: "pointer",
            textAlign: "left",
            border: activeFilter === "REJECTED" ? "2px solid #ef4444" : undefined,
            background: activeFilter === "REJECTED" ? "rgba(239, 68, 68, 0.08)" : undefined,
          }}
          onClick={() => setActiveFilter("REJECTED")}
        >
          <span className="cmts-kpi-card__label">Rejected / Failed</span>
          <strong style={{ color: metrics.rejected > 0 ? "#ef4444" : "#64748b" }}>{metrics.rejected}</strong>
          <span className="cmts-kpi-card__trend" style={{ fontSize: "0.75rem", color: metrics.rejected > 0 ? "#ef4444" : "#64748b" }}>Need rework</span>
        </button>
      </div>

      <div
        className="cmts-page-actions"
        style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", alignItems: "center", marginBottom: "1rem" }}
      >
        {complaintId ? (
          <Button
            label="Create Verification"
            variant="primary"
            disabled={
              !canCreate ||
              create.isPending
            }
            onClick={() => {
              setActionError(null);
              setCreateOpen(true);
            }}
          />
        ) : (
          <Button
            label="Open Complaints"
            variant="primary"
            onClick={() =>
              navigate(
                "/cmts/complaints",
              )
            }
          />
        )}

        <Button
          label="Export Summary"
          variant="secondary"
          disabled={displayRows.length === 0}
          onClick={handleExportSummary}
        />

        {activeFilter !== "ALL" ? (
          <Button
            label={`Clear Filter (${activeFilter})`}
            variant="ghost"
            onClick={() => setActiveFilter("ALL")}
          />
        ) : null}
      </div>

      <CmtsSmartDataGrid
      <
        VerificationDto
      >
        title="Verification Records"
        subtitle={
          activeFilter === "ALL"
            ? "Review governed verification assignments, decisions and audit-safe results."
            : `Showing filtered ${activeFilter} verification items (${displayRows.length} of ${rawRows.length}).`
        }
        rows={displayRows}
        columns={columns}
        rowKey="id"
        loading={list.isLoading}
        error={list.error}
        refreshable
        onRefresh={refresh}
        searchable
        sortable
        filterable
        emptyMessage={
          activeFilter === "ALL"
            ? "No verification records are available."
            : `No ${activeFilter} verification records found.`
        }
      />

      {/* Verification actions popup */}
      <CmtsEnterpriseModal
        open={Boolean(actionsFor)}
        title="Verification actions"
        subtitle={
          actionsFor
            ? `${actionsFor.verificationRuleName || "Verification"} • ${actionsFor.verifierName || "Unassigned verifier"}`
            : ""
        }
        size="md"
        onClose={() => {
          if (!busy) {
            setActionsFor(null);
            setActionError(null);
          }
        }}
      >
        {actionsFor && (
          <div className="cmts-notification-action-modal">
            <div className="cmts-notification-action-modal__summary">
              <div>
                <span>
                  Current status
                </span>

                <strong>
                  {statusName(
                    actionsFor,
                  )}
                </strong>
              </div>

              <div>
                <span>Verifier</span>

                <strong>
                  {actionsFor.verifierName ||
                    "Unassigned"}
                </strong>
              </div>

              <div>
                <span>Decision date</span>

                <strong>
                  {dateTime(
                    actionsFor.verifiedAt ??
                    actionsFor.rejectedAt,
                  )}
                </strong>
              </div>
            </div>

            {actionError ? (
              <div className="cmts-notification-action-modal__error">
                <CmtsApiErrorPanel
                  error={actionError}
                  title="Verification action failed"
                />
              </div>
            ) : null}

            <div className="cmts-notification-action-modal__grid">
              <VerificationActionCard
                icon="↗"
                title="Open complaint"
                description="Open the linked complaint verification workspace."
                disabled={
                  busy ||
                  !actionsFor.complaintId
                }
                onClick={() => {
                  const row =
                    actionsFor;

                  setActionsFor(null);

                  navigate(
                    `/cmts/complaints/${encodeURIComponent(
                      row.complaintId,
                    )}/verification`,
                  );
                }}
              />

              <VerificationActionCard
                icon="✓"
                title="Pass verification"
                description="Mark this verification as successful using the governed status."
                disabled={
                  busy ||
                  !selectedCanPass
                }
                onClick={() => {
                  const row =
                    actionsFor;

                  void runDecision(
                    "pass",
                    row,
                  );
                }}
              />
            </div>

            <div className="cmts-notification-action-modal__danger">
              <VerificationActionCard
                icon="×"
                title={
                  selectedDecisionCompleted
                    ? "Decision completed"
                    : "Fail verification"
                }
                description={
                  selectedDecisionCompleted
                    ? "This verification already has a terminal decision."
                    : "Mark this verification as failed using the governed failure status."
                }
                danger
                disabled={
                  busy ||
                  !selectedCanFail
                }
                onClick={() => {
                  const row =
                    actionsFor;

                  const confirmed =
                    globalThis.confirm(
                      "Do you want to mark this verification as failed?",
                    );

                  if (!confirmed) {
                    return;
                  }

                  void runDecision(
                    "fail",
                    row,
                  );
                }}
              />
            </div>
          </div>
        )}
      </CmtsEnterpriseModal>

      {/* Create verification modal */}
      <CmtsEnterpriseModal
        open={createOpen}
        title="Create Verification"
        subtitle="Assign a governed verification rule and an eligible independent verifier."
        size="lg"
        onClose={() => {
          if (!create.isPending) {
            setCreateOpen(false);
            setActionError(null);
          }
        }}
      >
        {complaintId ? (
          <CmtsVerificationForm
            complaintId={
              complaintId
            }
            verificationRules={
              rules.data ?? []
            }
            permittedVerifiers={
              verifiers.data ?? []
            }
            pendingStatusId={
              pendingStatusId
            }
            busy={
              create.isPending
            }
            onSubmit={async (
              payload,
            ) => {
              setActionError(null);

              await create.mutateAsync(
                payload,
              );
            }}
          />
        ) : null}
      </CmtsEnterpriseModal>
    </PageContainer>
  );
}